import { useEffect } from "react";
import debounce from "lodash/debounce";
import { shouldLoadMore } from "@/lib/client/utils";

/**
 * Hook for implementing infinite scroll functionality.
 *
 * Automatically triggers loadMore when user scrolls near the bottom of the page.
 * Uses debouncing to prevent excessive calls during rapid scrolling.
 *
 * @param loadMore - Function to call when more items should be loaded
 * @param hasMore - Whether there are more items to load
 * @param isLoading - Whether items are currently being loaded
 * @param threshold - Scroll threshold from bottom (0-1), default 0.5 (50%)
 *
 * @example
 * ```tsx
 * function ProductList() {
 *   const [items, setItems] = useState([]);
 *   const [page, setPage] = useState(1);
 *   const [hasMore, setHasMore] = useState(true);
 *   const [loading, setLoading] = useState(false);
 *
 *   const loadMore = async () => {
 *     if (loading || !hasMore) return;
 *     setLoading(true);
 *     const newItems = await fetchItems(page + 1);
 *     setItems(prev => [...prev, ...newItems]);
 *     setPage(p => p + 1);
 *     setHasMore(newItems.length > 0);
 *     setLoading(false);
 *   };
 *
 *   useInfiniteScroll(loadMore, hasMore, loading, 0.7);
 *
 *   return (
 *     <div>
 *       {items.map(item => <ProductCard key={item.id} {...item} />)}
 *       {loading && <Spinner />}
 *     </div>
 *   );
 * }
 * ```
 */
export const useInfiniteScroll = (
  loadMore: () => void,
  hasMore: boolean,
  isLoading: boolean,
  threshold = 0.5
) => {
  useEffect(() => {
    const handleScroll = debounce(() => {
      if (
        shouldLoadMore({
          scrollTop: window.scrollY,
          scrollHeight: document.documentElement.scrollHeight,
          clientHeight: window.innerHeight,
          threshold,
        }) &&
        hasMore &&
        !isLoading
      ) {
        loadMore();
      }
    }, 100);

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [loadMore, hasMore, isLoading, threshold]);
};
